import type { Entity, IEntityData, IFrameInfo, IFramePic, IPictureInfo, TFace } from "@/LFW";
import { Buff_Electroshock, clamp, cos, floor, LFW, max, abs, sin, World } from "@/LFW";
import type { IFrameModel, IFrameModelPose } from "@/LFW/defines/IFrameModel";
import type { IModelInfo } from "@/LFW/defines/IModelInfo";
import { Ditto } from "@/LFW/ditto";
import type { AnimationAction, AnimationClip, Bone } from "../_t";
import { AnimationMixer, BackSide, BufferGeometry, Color, Euler, LoopOnce, LoopRepeat, Mesh, MeshBasicMaterial, Object3D, Quaternion, ShaderMaterial, Vector3 } from "../_t";
import type { ImageMgr } from "../ImageMgr/ImageMgr";
import type { RImageInfo } from "../RImageInfo";
import type { EntityRenderer } from "./EntityRenderer";
import { MeshFactory, MeshKind } from "./factory";
import { OutlineMaterial } from "./materials/OutlineMaterial";
import { OutlineMesh } from "./meshs/OutlineMesh";
import { Shaders } from "./shader";
import { clone as clone_skeleton } from "three/addons/utils/SkeletonUtils.js";
import { SkinnedMesh } from "three";
import { ModelCache, type IModelCacheEntry } from "./ModelCache";
import type { WorldRenderer } from "./WorldRenderer";

const ATOM_PER_SECOND = 60;

const get_img_map = (lfw: LFW, data: IEntityData, out: Map<string, RImageInfo>): void => {
  out.clear();
  const { base: { files = {} } } = data;
  const images = lfw.images as ImageMgr;
  for (const key in files) {
    const img = images.find_by_pic_info(files[key]);
    img && out.set(key, img);
  }
};

function make_model_hull_material(center: Vector3): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      uOutline: { value: 0 },
      uCenter: { value: center },
      uColor: { value: new Color('#000000') },
      uAlpha: { value: 1 },
    },
    vertexShader: Shaders.Vertex.ModelOutline,
    fragmentShader: Shaders.Fragment.ModelOutline,
    transparent: true,
    depthWrite: false,
    // 只渲染外扩壳的背面(far side)：正面更靠近相机、会盖住模型，背面才在轮廓外露出
    side: BackSide,
  })
}

function build_model_hulls(root: Object3D): Mesh[] {
  // 先收集目标 mesh，遍历结束后再挂 hull。
  // 若在 traverse 回调里给 mesh.add(hull)，three 的 traverse 会继续下钻到刚
  // 挂上的 hull（它同样带 normal 的 Mesh）再挂一层……无限递归导致卡死。
  const meshes: Mesh[] = []
  root.traverse(obj => {
    const mesh = obj as Mesh
    if (mesh.isMesh) meshes.push(mesh)
  })
  const hulls: Mesh[] = []
  for (const mesh of meshes) {
    const geo = mesh.geometry as BufferGeometry
    if (!geo?.getAttribute?.('normal')) continue
    // 外扩中心取 mesh 自身几何包围盒中心，而非模型原点：
    // 封闭外壳始终围绕自身，单 mesh 物品（盒体/球等）硬边也能得到连续描边
    geo.computeBoundingBox()
    const center = new Vector3()
    geo.boundingBox?.getCenter(center)
    const material = make_model_hull_material(center)
    const skinned_src = mesh as SkinnedMesh
    const hull: Mesh = skinned_src.isSkinnedMesh
      ? new SkinnedMesh(geo, material)
      : new Mesh(geo, material)
    if (skinned_src.isSkinnedMesh) {
      const skinned = hull as SkinnedMesh
      skinned.bindMode = skinned_src.bindMode
      skinned.bind(skinned_src.skeleton, skinned_src.bindMatrix)
    }
    hull.name = 'model_outline_hull'
    hull.visible = false
    // 挂到 mesh 节点自身之下而非其兄弟节点：mesh 节点通常自带 rotation/
    // translation（如 book_bundle 的 Cube 节点带旋转），兄弟节点不会继承该
    // 变换，会导致外壳与模型错位。作为子节点则继承节点变换，且顶点偏移仍
    // 在 mesh 局部空间进行（偏移方向随模型一起旋转，保持均匀外扩）。
    mesh.add(hull)
    hulls.push(hull)
  }
  return hulls
}


export class EntityMainRender {
  readonly world_renderer: WorldRenderer;
  readonly owner: EntityRenderer;
  readonly world: World;
  readonly lfw: LFW;
  protected images = new Map<string, RImageInfo>();
  protected entity: Entity;
  protected node = new Object3D();
  protected meshs: OutlineMesh[] = [
    MeshFactory.get(MeshKind.Entity, OutlineMesh)
  ];
  protected blood_mesh = MeshFactory.get(MeshKind.Blood, Mesh<BufferGeometry, MeshBasicMaterial>);
  protected file_variants = new Map<string, string[]>();
  protected shaking = 0;
  protected shaking_x = 0;
  protected data: IEntityData;
  protected frame: IFrameInfo;
  protected facing: TFace;
  protected centerx = 0;
  protected centery = 0;
  protected files: Record<string, IPictureInfo> = {};
  protected models: Record<string, IModelInfo> = {};
  protected model_variants = new Map<string, string[]>();
  protected model_node = new Object3D();
  protected model_key = "";
  protected model_path = "";
  protected model_hulls: Mesh[] = [];
  protected model_sx = 1;
  protected model_outline_key = "";
  protected mixer: AnimationMixer | undefined;
  protected mixer_actions = new Map<string, AnimationAction>();
  protected bone_list: Bone[] = [];
  protected bone_by_name = new Map<string, Bone>();
  protected _q1 = new Quaternion();
  protected _q2 = new Quaternion();
  // 模型旋转合成（base*帧）临时量
  protected _rot_qa = new Quaternion();
  protected _rot_qb = new Quaternion();
  protected _rot_ea = new Euler();
  protected _rot_eb = new Euler();
  protected _rot_e = new Euler();
  protected _hull_scale = new Vector3();
  protected playing_anim = "";
  protected anim_loop = false;
  protected anim_mapped = false;
  protected anim_speed = 1;
  protected prev_lifetime = 0;
  protected img: RImageInfo | undefined;
  protected img_pending = new Set<string>();
  protected pinned_keys: string[] = [];
  protected render_effect_time = -1;
  protected variant: number = -1;

  constructor(owner: EntityRenderer) {
    this.owner = owner;
    this.world_renderer = owner.owner;
    const { entity } = owner;
    this.entity = entity;
    this.lfw = entity.lfw;
    this.world = entity.world;
    this.data = entity.data;
    this.frame = entity.frame;
    this.facing = entity.facing;
    this.node.add(this.meshs[0], this.blood_mesh, this.model_node)
  }

  reset(): void {
    const { entity } = this;
    this.frame = entity.frame;
    this.facing = entity.facing;
    this.shaking = 0;
    this.shaking_x = 0;
    this.render_effect_time = -1;
    const { data } = entity;
    this.file_variants.clear();
    const files = this.files = data.base.files ?? {};
    for (const k in files) {
      const file = files[k];
      file.variants?.length && this.file_variants.set(k, [k, ...file.variants]);
    }

    const model_list: IModelInfo[] = [];
    const models = this.models = data.base.models ?? {};
    for (const k in models) {
      const model = models[k];
      model.variants?.length && this.model_variants.set(k, [k, ...model.variants]);
      model_list.push(model);
    }

    this.data = data;
    get_img_map(this.lfw, data, this.images);
    this.pin_files();
    // 贴图与全局 ImageMgr 共享（clip 走 uniforms，无需隔离 texture）。
    // 不要在这里设 needsUpdate：它会 bump 共享 source.version，
    // 导致所有引用同一贴图的实体都在下一帧重新上传 GPU。

    this.dispose_model();
    for (const info of model_list) this.preload_model(info);
    this.meshs[0].visible = false;
    this.meshs[0].name = `Entity Mesh 0: ${this.entity.name}`;

    const mesh_count = this.meshs.length;
    const pics_count = this.data.__pics || 0;
    const count = max(mesh_count, pics_count)
    for (let i = 0; i < count; ++i) {
      let mesh = this.meshs[i + 1];
      if (i <= pics_count) {
        if (!mesh) {
          mesh = MeshFactory.get(MeshKind.Entity, OutlineMesh);
          this.meshs[i + 1] = mesh;
          this.node.add(mesh)
        }
        mesh.name = `Entity Mesh ${i}: ${this.entity.name}`
        mesh.visible = false;
      } else if (mesh) {
        mesh.removeFromParent();
      }
    }
    this.meshs.length = (pics_count + 1);
    for (const mesh of this.meshs) mesh.reset();

    this.blood_mesh.visible = false;
    this.blood_mesh.name = `Blood: ${this.entity.name}`;
  }

  on_mount(): void {
    this.reset()
    this.update_texture();
    this.update_outline();
    this.owner.body.add(this.node);
  }

  /** 当前帧是否正在显示 3D 模型（决定是否启用逐实体深度隔离渲染） */
  get model_active(): boolean {
    return this.model_node.visible;
  }

  on_unmount(): void {
    this.node.removeFromParent();
    const images = this.lfw.images as ImageMgr;
    for (const key of this.pinned_keys) images.unpin(key);
    this.pinned_keys.length = 0;
  }

  private pin_files(): void {
    const images = this.lfw.images as ImageMgr;
    for (const key of this.pinned_keys) images.unpin(key);
    this.pinned_keys.length = 0;
    for (const k in this.files) {
      const key = images.key_of(this.files[k]);
      images.pin(key);
      this.pinned_keys.push(key);
    }
  }

  private request_img(tex: string, info: IPictureInfo): void {
    if (this.img_pending.has(tex)) return;
    this.img_pending.add(tex);
    const images = this.lfw.images as ImageMgr;
    images.retain_by_pic_info(info).then((loaded) => {
      this.img_pending.delete(tex);
      if (loaded) this.images.set(tex, loaded);
      this.update_texture();
    }).catch((e) => {
      this.img_pending.delete(tex);
      Ditto.warn('[EntityMainRender::request_img]', e);
    });
  }

  update_shaking(): void {
    let { shaking, facing, buffs } = this.entity;

    if (!shaking && buffs.size) {
      for (const [, b] of buffs) {
        if (b.kind === Buff_Electroshock.KIND) {
          shaking = b.duration - b.lifetime;
          break;
        }
      }
    }
    if (shaking != this.shaking) {
      this.shaking = shaking;
      this.shaking_x = shaking ? facing * (floor(shaking / 2) % 2 ? 1 : -1) : 0;
    }
  }

  render(): void {
    const { entity, meshs } = this;
    if (this.world_renderer.dirty) {
      const { frame, facing, data, variant } = entity;
      if (data != this.data) {
        this.reset();
        this.update_texture();
        this.update_outline();
      } else if (this.frame !== frame || this.facing !== facing || this.variant !== variant) {
        this.frame = frame;
        this.facing = facing;
        this.variant = variant;
        this.update_texture();
      }
    }

    this.update_shaking();
    this.node.position.copy(this.owner.position);
    const { invisible } = this.owner;
    const { blinking, facing } = entity;
    const visible = !invisible && (!blinking || floor(blinking / 4) % 2 === 0)

    // 3D 模型与 2D 精灵可共存：frame 同时有 pic 和 model 时叠加渲染
    const model = this.get_frame_model(this.frame)
    if (model) {
      this.update_model(model)
      this.update_model_visual(model)
      const base = this.models[model.id]
      const b = base?.scale
      const s = model.scale
      const sx = (b?.x ?? 1) * (s?.x ?? 1)
      const sy = (b?.y ?? 1) * (s?.y ?? 1)
      const sz = (b?.z ?? 1) * (s?.z ?? 1)
      this.model_sx = Math.max(Math.abs(sx), 1e-4)
      // 平移 = base 模型 offset + 帧 model offset（缺省 0，逐轴相加，y 向上为正）
      const bo = base?.offset
      const fo = model.offset
      const ox = (bo?.x ?? 0) + (fo?.x ?? 0)
      const oy = (bo?.y ?? 0) + (fo?.y ?? 0)
      const oz = (bo?.z ?? 0) + (fo?.z ?? 0)
      // 旋转 = base 模型 rotation * 帧 rotation（帧作用于模型自身坐标系；rad 兼容为 rotation.z）
      const br = base?.rotation
      const fr = model.rotation
      this._rot_ea.set(br?.x ?? 0, br?.y ?? 0, br?.z ?? 0)
      this._rot_eb.set(fr?.x ?? 0, fr?.y ?? 0, fr?.z ?? (model.rad ?? 0))
      this._rot_qa.setFromEuler(this._rot_ea)
      this._rot_qb.setFromEuler(this._rot_eb)
      this._rot_qa.multiply(this._rot_qb)
      this._rot_e.setFromQuaternion(this._rot_qa)
      // 朝向镜像（scale.x<0，模型绕 X 轴不变、Y/Z 反向）
      this.model_node.visible = visible
      this.model_node.position.set(this.shaking_x + ox, oy, oz)
      this.model_node.scale.set(facing * sx, sy, sz)
      this.model_node.rotation.set(
        this._rot_e.x,
        facing < 0 ? -this._rot_e.y : this._rot_e.y,
        facing < 0 ? -this._rot_e.z : this._rot_e.z,
      )
      this.update_model_outline()
    } else {
      this.model_node.visible = false
    }

    const { pic } = this.frame;
    const mesh = meshs[0]
    mesh.visible = visible && !(model && !pic);

    if (pic) {
      const cx = this.centerx + this.shaking_x;
      const cy = this.centery;
      this.update_mesh_position(pic, mesh, cx, cy, 0)
    }

    for (let i = 1; i < meshs.length; i++) {
      const mesh = meshs[i];
      if (!mesh) continue;
      mesh.visible = visible;
      if (!visible) continue;
      const pic = this.frame.pics?.[i - 1]
      if (!pic) mesh.visible = false;
      if (!pic) continue;
      const cx: number = (
        (pic?.cx === void 0) ? this.centerx :
          (facing === 1 ? -pic.cx : pic.cx - pic.w)
      ) + this.shaking_x;
      const cy = pic.cy ?? this.centery;
      // stupid -Gim
      this.update_mesh_position(pic, mesh, cx, cy, 0.05 * i)
    }

    this.render_bpoint();
    this.update_outline();
  }

  /** 获取当前帧要使用的模型（含队伍变体解析） */
  private get_frame_model(frame: IFrameInfo): IFrameModel | undefined {
    const m = frame.model
    if (!m) return void 0
    const { variant } = this.entity
    if (variant) {
      const id = this.model_variants.get(m.id)?.at(variant)
      if (id) return { ...m, id }
    }
    return m
  }

  /** 切换/加载模型（含缓存；姿态/片段驱动在 update_model_visual） */
  private update_model(model: IFrameModel): void {
    if (this.model_key === model.id) return
    this.model_key = model.id
    const info = this.models[model.id]
    if (!info) {
      this.model_node.visible = false
      Ditto.warn(`[EntityMainRender] model "${model.id}" 未在 base.models 中声明`)
      return
    }
    const { path } = this.resolve_model_path(info)
    const cached = ModelCache.instance.peek(path)
    if (cached) {
      this.attach_model(path, cached)
      return
    }
    // 未缓存：保留旧模型（若有）直至新模型加载完成，避免切换瞬间闪没
    void this.load_model(info)
  }

  /** 预加载模型到缓存（不挂载），消除首次渲染的加载空窗 */
  private preload_model(info: IModelInfo): void {
    void this.load_model(info)
  }

  /** 解析 info.path 的查找前缀（! 精确 / ? 模糊） */
  private resolve_model_path(info: IModelInfo): { path: string; exact: boolean } {
    let path = info.path
    let exact = true
    if (path.startsWith('!')) path = path.substring(1)
    else if (path.startsWith('?')) { path = path.substring(1); exact = false }
    return { path, exact }
  }

  /** 异步加载 GLB 到全局缓存；加载完成时若仍是当前需要的模型则挂载 */
  private async load_model(info: IModelInfo): Promise<void> {
    const { path, exact } = this.resolve_model_path(info)
    try {
      const entry = await ModelCache.instance.get(this.lfw, path, exact)
      if (this.models[this.model_key] === info) {
        this.attach_model(path, entry)
      }
    } catch (e) {
      Ditto.warn(`[EntityMainRender] 3D 模型加载失败: ${info.path}`, e)
      ModelCache.instance.invalidate(path)
      // 允许后续帧重试（否则 model_key 已切换且加载失败，模型将永久不显示）
      if (this.model_key === info.id) this.model_key = ""
    }
  }

  private attach_model(path: string, cache: IModelCacheEntry): void {
    if (this.model_path === path) return
    const old_path = this.model_path
    if (old_path) {
      this.model_node.clear()
      this.dispose_model_hulls()
      ModelCache.instance.release(old_path)
    }
    ModelCache.instance.retain(path)
    // 缓存 root 是共享模板（同一 Object3D 只能有一个父节点），直接 add 会被后挂载者抢走 → 只剩一个可见。
    // 每个实体克隆一棵场景图：SkeletonUtils.clone 几何/材质仍共享，蒙皮会克隆并重绑骨架供逐实体动画。
    const clone = clone_skeleton(cache.root)
    this.model_hulls = build_model_hulls(clone)
    this.model_outline_key = ""
    this.model_node.add(clone)
    this.model_node.visible = true
    this.collect_bones(clone)
    this.setup_mixer(clone, cache.animations)
    this.playing_anim = ""
    this.prev_lifetime = this.entity.lifetime
    this.model_path = path
  }

  /** 每帧驱动模型视觉：姿态插值（模式A）与动画片段（模式B） */
  private update_model_visual(model: IFrameModel): void {
    const sim_dt = (this.entity.lifetime - this.prev_lifetime) / ATOM_PER_SECOND
    this.prev_lifetime = this.entity.lifetime

    const total = this.frame.wait
    const t = total > 0 ? clamp(1 - this.entity.wait / total, 0, 1) : 1

    if (model.pose) {
      this.apply_pose_blend(model.pose, this.get_next_model_pose(), t)
    }
    if (model.anim) {
      if (model.seek !== void 0) this.play_anim_segment(model, t)
      else this.play_anim(model, sim_dt)
    } else {
      this.stop_anim()
    }
  }

  private get_next_frame(): IFrameInfo | undefined {
    const next = this.frame.next
    if (!next) return void 0
    const id = typeof next === 'string' ? next : Array.isArray(next) ? (next[0] as any)?.id : (next as any)?.id
    if (!id) return void 0
    return this.data.frames?.[id]
  }

  /** 下一帧的姿态（用于当前帧姿态向下一帧插值） */
  private get_next_model_pose(): IFrameModelPose | undefined {
    return this.get_next_frame()?.model?.pose
  }

  /** 应用姿态（与下一帧姿态按 t 混合，t∈[0,1]） */
  private apply_pose_blend(from: IFrameModelPose, to: IFrameModelPose | undefined, t: number): void {
    const bones = this.pose_bones(from)
    if (!bones.length) return
    const { rot, pos, scl } = from
    const rot2 = to?.rot, pos2 = to?.pos, scl2 = to?.scl
    const has_to = !!(to && (rot2 || pos2 || scl2))
    for (let i = 0; i < bones.length; ++i) {
      const bone = bones[i]
      if (rot && rot.length >= (i + 1) * 4) {
        const q = this._q1.set(rot[i * 4], rot[i * 4 + 1], rot[i * 4 + 2], rot[i * 4 + 3])
        if (has_to && rot2 && rot2.length >= (i + 1) * 4) {
          this._q2.set(rot2[i * 4], rot2[i * 4 + 1], rot2[i * 4 + 2], rot2[i * 4 + 3])
          q.slerp(this._q2, t)
        }
        bone.quaternion.copy(q)
      }
      if (pos && pos.length >= (i + 1) * 3) {
        let x = pos[i * 3], y = pos[i * 3 + 1], z = pos[i * 3 + 2]
        if (has_to && pos2 && pos2.length >= (i + 1) * 3) {
          x += (pos2[i * 3] - x) * t
          y += (pos2[i * 3 + 1] - y) * t
          z += (pos2[i * 3 + 2] - z) * t
        }
        bone.position.set(x, y, z)
      }
      if (scl && scl.length >= (i + 1) * 3) {
        let x = scl[i * 3], y = scl[i * 3 + 1], z = scl[i * 3 + 2]
        if (has_to && scl2 && scl2.length >= (i + 1) * 3) {
          x += (scl2[i * 3] - x) * t
          y += (scl2[i * 3 + 1] - y) * t
          z += (scl2[i * 3 + 2] - z) * t
        }
        bone.scale.set(x, y, z)
      }
    }
  }

  /** 按 pose.bones（名称）或骨骼遍历顺序取骨骼列表 */
  private pose_bones(pose: IFrameModelPose): Bone[] {
    const names = pose.bones
    if (names?.length) {
      const ret: Bone[] = []
      for (const n of names) {
        const b = this.bone_by_name.get(n)
        if (b) ret.push(b)
      }
      return ret
    }
    return this.bone_list
  }

  /** 收集模型根下的骨骼 */
  private collect_bones(root: Object3D): void {
    this.bone_list = []
    this.bone_by_name.clear()
    root.traverse(obj => {
      const bone = obj as Bone
      if (bone.isBone) {
        this.bone_list.push(bone)
        if (obj.name) this.bone_by_name.set(obj.name, bone)
      }
    })
  }

  /** 用 glTF 动画片段初始化 mixer */
  private setup_mixer(root: Object3D, animations: AnimationClip[]): void {
    this.mixer = new AnimationMixer(root)
    this.mixer_actions.clear()
    for (const clip of animations) {
      this.mixer_actions.set(clip.name, this.mixer.clipAction(clip))
    }
  }

  /** 播放/推进动画片段（确定性：用实体 lifetime 差作为模拟时钟） */
  private play_anim(model: IFrameModel, sim_dt: number): void {
    if (!this.mixer) return
    const name = model.anim ?? ''
    const loop = !!model.loop
    const speed = model.time_scale ?? 1
    const hold_last = !loop && !!model.hold_last
    if (this.playing_anim !== name || this.anim_loop !== loop || this.anim_mapped) {
      this.playing_anim = name
      this.anim_loop = loop
      this.anim_mapped = false
      this.anim_speed = speed
      for (const [, a] of this.mixer_actions) a.stop()
      const action = this.mixer_actions.get(name)
      if (action) {
        action.setLoop(loop ? LoopRepeat : LoopOnce, loop ? Infinity : 1)
        action.timeScale = speed
        action.reset().play()
      }
    } else if (this.anim_speed !== speed) {
      this.anim_speed = speed
      this.mixer_actions.get(name)?.setEffectiveTimeScale(speed)
    }
    const action = this.mixer_actions.get(name)
    if (action) action.clampWhenFinished = hold_last
    this.mixer.update(sim_dt)
  }

  private play_anim_segment(model: IFrameModel, t: number): void {
    const action = this.ensure_anim_action(model)
    if (!action) return
    const duration = action.getClip().duration
    const seek = Math.max(model.seek ?? 0, 0)
    const next = this.get_next_frame()?.model
    const same_clip = !!next && next.anim === model.anim && next.id === model.id
    let end = model.seek_end ?? (same_clip && next!.seek !== void 0 ? Math.max(next!.seek ?? 0, 0) : duration)
    let seg_len = end - seek
    if (!(seg_len > 0)) seg_len = duration - seek
    if (!(seg_len > 0)) seg_len = 0
    let u = t * (model.time_scale ?? 1)
    if (model.loop) u = u - Math.floor(u)
    else u = clamp(u, 0, 1)
    action.time = seek + u * seg_len
    this.mixer?.update(0)
  }

  private ensure_anim_action(model: IFrameModel): AnimationAction | undefined {
    if (!this.mixer) return void 0
    const name = model.anim ?? ''
    const loop = !!model.loop
    if (this.playing_anim !== name || this.anim_loop !== loop || !this.anim_mapped) {
      this.playing_anim = name
      this.anim_loop = loop
      this.anim_mapped = true
      this.anim_speed = 1
      for (const [, a] of this.mixer_actions) a.stop()
      const action = this.mixer_actions.get(name)
      if (action) {
        action.setLoop(loop ? LoopRepeat : LoopOnce, loop ? Infinity : 1)
        action.timeScale = 1
        action.reset().play()
        action.paused = true
      }
    }
    return this.mixer_actions.get(name)
  }

  private stop_anim(): void {
    if (!this.playing_anim) return
    this.playing_anim = ""
    this.anim_mapped = false
    for (const [, a] of this.mixer_actions) a.stop()
  }

  /** 卸载模型节点、释放引用（全局缓存决定是否回收 GPU 资源） */
  private dispose_model(): void {
    this.model_key = ""
    this.model_node.visible = false
    this.mixer?.stopAllAction()
    this.mixer = undefined
    this.mixer_actions.clear()
    this.playing_anim = ""
    this.bone_list = []
    this.bone_by_name.clear()
    if (this.model_path) {
      ModelCache.instance.release(this.model_path)
      this.model_path = ""
    }
    this.model_node.clear()
    this.dispose_model_hulls()
    this.model_sx = 1
    this.model_outline_key = ""
  }

  private dispose_model_hulls(): void {
    for (const hull of this.model_hulls) {
      (hull.material as ShaderMaterial)?.dispose?.()
    }
    this.model_hulls = []
  }

  private update_model_outline(): void {
    const hulls = this.model_hulls
    if (!hulls.length) return
    const { ghosted, outline_color, outline_alpha, outline_width, outline_enabled } = this.entity
    const show = !ghosted && !!outline_color && !!outline_alpha && !!outline_enabled
    const key = `${show}:${outline_color || ''}:${outline_alpha}:${outline_width}:${outline_enabled}:${this.model_sx}`
    if (key === this.model_outline_key) return
    this.model_outline_key = key
    const width_world = show ? outline_width + 1 : 0
    const scale = this._hull_scale
    for (const hull of hulls) {
      const m = hull.material as ShaderMaterial
      if (hull.parent) {
        hull.parent.updateWorldMatrix(true, false)
        hull.parent.getWorldScale(scale)
      } else {
        scale.set(1, 1, 1)
      }
      const k = max(abs(scale.x), abs(scale.y), abs(scale.z), 1e-6)
      m.uniforms.uOutline.value = width_world / k
      m.uniforms.uColor.value.set(outline_color || '#000')
      m.uniforms.uAlpha.value = show ? outline_alpha : 0
      // 恒定 BackSide：three 会按 matrixWorld 行列式为负自动翻转 frontFace，
      // 镜像(scale.x=-1)时仍渲染世界背面(far side)，不会把外扩正面盖到模型上
      m.side = BackSide
      hull.visible = show
    }
  }

  private update_mesh_position(pic: IFramePic, mesh: Mesh<BufferGeometry, OutlineMaterial>, cx: number, cy: number, cz: number) {
    const rad = pic?.rad
    if (!pic || !rad) {
      mesh.position.x = cx;
      mesh.position.y = cy;
      mesh.position.z = cz;
      mesh.rotation.z = 0;
      return;
    }
    const ox = pic?.ox ?? pic.w / 2;
    const oy = pic?.oy ?? pic.h / 2;
    const px = cx + ox;
    const py = cy - oy;
    const dx = cx - px;
    const dy = cy - py;
    const flipped = this.facing < 0;
    const _cos = pic.__cos_r ?? cos(rad);
    const _sin = (pic.__sin_r ?? sin(rad)) * (flipped ? -1 : 1);
    mesh.position.x = px + dx * _cos - dy * _sin;
    mesh.position.y = py + dx * _sin + dy * _cos;
    mesh.position.z = cz;
    mesh.rotation.z = flipped ? -rad : rad;
  }
  private update_mesh_material(pic: IFramePic, mesh: Mesh<BufferGeometry, OutlineMaterial>) {
    mesh.scale.set(pic.w, pic.h, 0);
    const { entity } = this;
    const { variant } = entity;
    const { material: m } = mesh;
    let { tex } = pic;
    if (variant) tex = this.file_variants.get(tex)?.at(variant) ?? tex;
    const { images } = this;
    const img = this.img = images.get(tex);
    if (img?.pic) {
      m.texture = img.pic.texture;
      m.set_tex_size(img.w, img.h, img.scale)
    } else {
      m.texture = void 0;
      const info = this.files[tex];
      if (info) this.request_img(tex, info);
    }
    m.set_clip(pic.x, pic.y, pic.w, pic.h)
    m.flip_x = entity.facing;
  }

  private update_texture() {
    const { meshs, entity } = this;
    const { frame, facing } = entity;
    const { centerx, centery, width } = frame;
    const { pic, pics } = frame;
    this.centerx = facing === 1 ? -centerx : centerx - width;
    this.centery = centery;
    if (pic) this.update_mesh_material(pic, this.meshs[0])
    for (let i = 0; pics && i < pics?.length; ++i) {
      const pic = pics[i];
      const mesh = meshs[i + 1];
      if (!pic || !mesh) continue;
      this.update_mesh_material(pic, mesh);
    }
  }


  private update_outline(): void {
    const { ghosted, render_effect_time } = this.entity;
    if (ghosted) return;

    if (this.render_effect_time == render_effect_time) return;
    this.render_effect_time = render_effect_time;
    const mesh0 = this.meshs[0];
    const { material: m } = mesh0;
    const {
      outline_color,
      outline_alpha,
      outline_width,
      outline_enabled,
      greyscale,
      mix_color,
      mix_strength
    } = this.entity;

    if (outline_color && outline_alpha && outline_enabled) {
      m.outlineColor.set(outline_color);
      m.outlineAlpha = outline_alpha;
      m.outlineWidth = outline_width * (this.img?.scale ?? 1)
    } else {
      m.outlineWidth = 0;
      m.outlineAlpha = 0;
    }
    m.gray = greyscale;
    if (mix_strength) {
      m.mixColor.set(mix_color);
      m.mixStrength = mix_strength;
    } else {
      m.mixStrength = 0;
    }
  }

  private render_bpoint(): void {
    const { entity } = this;
    const { bpoint } = entity.frame;
    const mesh0 = this.meshs[0]
    const visible = !!bpoint && mesh0.visible && entity.hp < entity.hp_max * 0.33;
    this.blood_mesh.visible = visible;

    if (!visible) return;

    let { x: bx, y: by, z: bz = 0.1, r = 0 } = bpoint;
    bx = entity.facing === 1 ? bx : mesh0.scale.x - bx;
    this.blood_mesh.position.set(this.centerx + bx, this.centery - by, bz);
  }
}


